import { useIsWideMainLayout, useWideMainLayout } from 'features/layout'

import { useCenterPanelLayout, useCenterPanelLayoutMode } from './centerPanelLayoutContext'
import { useProductRefreshAppShell } from './useProductRefreshAppShell'

/**
 * Declares that the current route wants the center panel in wide mode.
 * - **New app shell:** `useCenterPanelLayout('wide')` — center panel expands to 2560px max.
 * - **Legacy shell:** `useWideMainLayout` — MainRegion expands to 2560px max.
 * Only one path is active; gated by `useProductRefreshAppShell()`.
 *
 * @param enabled — when `false`, wide mode is not applied (e.g. feature-gated pages).
 */
export const useWideCenterPanelLayout = (enabled = true): void => {
  const isProductRefreshAppShell = useProductRefreshAppShell()
  const legacyWide = !isProductRefreshAppShell && enabled
  const appShellWide = isProductRefreshAppShell && enabled
  // When new shell is active, explicitly pass false so legacy MainRegion context
  // (which defaults to a no-op setter) stays clear. Safe to remove when legacy is deleted.
  useWideMainLayout(legacyWide)
  useCenterPanelLayout('wide', appShellWide)
}

/**
 * Reads whether the center panel is currently in wide mode.
 * Symmetric counterpart to `useWideCenterPanelLayout`.
 */
export const useIsWideCenterPanelLayout = (): boolean => {
  const isProductRefreshAppShell = useProductRefreshAppShell()
  const [isLegacyWide] = useIsWideMainLayout()
  const centerLayout = useCenterPanelLayoutMode()
  return isProductRefreshAppShell ? centerLayout === 'wide' : isLegacyWide
}
